import { bindScrollSafeOverlay, unbindScrollSafeOverlay } from '@classic/Shared/OverlayPosition';

const MORE_LINK_SELECTOR = 'button, [role="button"], .fc-more-link';

let activeTrigger: HTMLElement | undefined;
const moreLinkClicks = new WeakMap<HTMLElement, () => void>();

export const bindMoreLink = (element: HTMLElement): void => {
    unbindMoreLinkClick(element);
    const onClick = (): void => {
        scheduleMorePopoverPosition(element);
    };
    element.addEventListener('click', onClick);
    moreLinkClicks.set(element, onClick);
};

export const scheduleMorePopoverPosition = (jsEventOrTrigger: UIEvent | HTMLElement): void => {
    const trigger = jsEventOrTrigger instanceof HTMLElement
        ? jsEventOrTrigger
        : moreLinkTriggerFromEvent(jsEventOrTrigger);
    if (!trigger) {
        return;
    }

    const tryBind = (): boolean => {
        if (!findMorePopover(trigger)) {
            return false;
        }
        bindMorePopoverPosition(trigger);
        return true;
    };

    if (tryBind()) {
        return;
    }

    // moreLinkClick / the native click run before FC setStates the dialog into
    // the DOM. Watch for the portal, and keep a short rAF fallback.
    const observer = new MutationObserver(() => {
        if (tryBind()) {
            observer.disconnect();
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    let remaining = 10;
    const tick = (): void => {
        if (tryBind() || remaining <= 0) {
            observer.disconnect();
            return;
        }
        remaining -= 1;
        requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
};

export const bindMorePopoverPosition = (trigger: HTMLElement): void => {
    if (activeTrigger && activeTrigger !== trigger) {
        unbindScrollSafeOverlay(activeTrigger);
    }
    activeTrigger = trigger;
    bindScrollSafeOverlay(
        trigger,
        () => findMorePopover(trigger),
        () => (trigger.isConnected ? trigger : expandedMoreLink() ?? trigger),
    );
};

export const unbindMoreLink = (element: HTMLElement): void => {
    unbindMoreLinkClick(element);
    if (activeTrigger !== element) {
        return;
    }
    // FC remounts more-links when events refetch. The dialog can stay open
    // while the old control is detached (0×0 at the origin — CRO-14575).
    const live = expandedMoreLink();
    if (live && findMorePopover(live)) {
        bindMorePopoverPosition(live);
        return;
    }
    unbindActiveMorePopover();
};

export const unbindActiveMorePopover = (): void => {
    if (!activeTrigger) {
        return;
    }
    unbindScrollSafeOverlay(activeTrigger);
    activeTrigger = undefined;
};

export const findMorePopover = (trigger: HTMLElement): HTMLElement | undefined => {
    const id = trigger.getAttribute('aria-controls');
    if (id) {
        const byId = document.getElementById(id);
        if (byId instanceof HTMLElement) {
            return byId;
        }
    }
    const dialog = document.body.querySelector('[role="dialog"][data-date]');
    return dialog instanceof HTMLElement ? dialog : undefined;
};

export const moreLinkTriggerFromEvent = (jsEvent: UIEvent): HTMLElement | undefined => {
    if (isHtmlElement(jsEvent.currentTarget)) {
        const fromCurrent = jsEvent.currentTarget.closest(MORE_LINK_SELECTOR);
        if (fromCurrent instanceof HTMLElement) {
            return fromCurrent;
        }
        return jsEvent.currentTarget;
    }
    const raw = jsEvent.target;
    const el = raw instanceof Text ? raw.parentElement : isHtmlElement(raw) ? raw : undefined;
    if (!el) {
        return expandedMoreLink();
    }
    const button = el.closest(MORE_LINK_SELECTOR);
    return button instanceof HTMLElement ? button : expandedMoreLink();
};

const expandedMoreLink = (): HTMLElement | undefined => {
    const expanded = document.querySelector(`${MORE_LINK_SELECTOR}[aria-expanded="true"]`);
    return expanded instanceof HTMLElement ? expanded : undefined;
};

const unbindMoreLinkClick = (element: HTMLElement): void => {
    const onClick = moreLinkClicks.get(element);
    if (!onClick) {
        return;
    }
    element.removeEventListener('click', onClick);
    moreLinkClicks.delete(element);
};

const isHtmlElement = (value: EventTarget | null): value is HTMLElement => value instanceof HTMLElement;
